import React, { useState } from 'react';
import { toast } from 'react-toastify';
import useApiData, { apiFetch, apiErrorMessage } from '../hooks/useApi';
import { formatDate } from '../utils/dates';
import { plural } from '../utils/format';

/**
 * <JobInductionPack /> — what someone needs before they set foot on this job's site (FSM 3.8):
 * the client's induction (link, renewal), PPE, pre-site checks, client documents, which forms
 * to fill in, and the job description. Comes from the client record linked to the job's
 * building (Clients → access and documents).
 *
 * variant="workspace" (default) uses the workspace cw-* styles and shows the team's induction
 * status; variant="field" is the compact version for the engineer's own job view, styled
 * inline so it works outside the workspace.
 */

const EMPTY = { packs: [], forms: [], team: [], job: null, me: null, can_record: false, can_record_others: false };

const field = {
  card: { border: '1px solid #e5e7eb', borderRadius: 12, padding: 14, margin: '12px 0', background: '#fff' },
  h: { margin: '0 0 8px', fontSize: '1rem', fontWeight: 700 },
  h4: { margin: '12px 0 6px', fontSize: '0.85rem', fontWeight: 700, color: '#374151' },
  meta: { fontSize: '0.8rem', color: '#6b7280' },
  btn: { minHeight: 44, padding: '8px 14px', borderRadius: 8, border: '1px solid #1A80E5', background: '#1A80E5', color: '#fff', fontWeight: 600, cursor: 'pointer' },
  link: { color: '#1565C0', fontWeight: 600 },
};

const List = ({ items, isField, icon }) => (
  <ul style={{ margin: 0, paddingLeft: isField ? 18 : 0, listStyle: isField ? 'disc' : 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
    {items.map((t) => (
      <li key={t} style={{ fontSize: isField ? '0.9rem' : 'var(--cs-text-sm)' }}>
        {!isField && <i className={icon} aria-hidden="true" style={{ color: 'var(--cw-blue)', width: 18 }} />}{t}
      </li>
    ))}
  </ul>
);

const stateText = (st) => {
  if (!st || st.state === 'missing') return 'Not done yet';
  if (st.state === 'expired') return `Expired ${formatDate(st.expires_on)}`;
  return `Done ${formatDate(st.inducted_on)}${st.expires_on ? ` · renew by ${formatDate(st.expires_on)}` : ''}`;
};

const JobInductionPack = ({ jobId, variant = 'workspace', readOnly = false }) => {
  const isField = variant === 'field';
  const { data, error, reload, hasLoaded } = useApiData(jobId ? `/api/contractor/jobs/${jobId}/induction-pack` : null, EMPTY, (d) => ({ ...EMPTY, ...(d || {}) }));
  const [busy, setBusy] = useState(null);

  const record = async (clientId, userId) => {
    setBusy(`${clientId}:${userId || 'me'}`);
    try {
      await apiFetch(`/api/contractor/jobs/${jobId}/induction-pack/inducted`, { method: 'POST', body: JSON.stringify({ client_id: clientId, user_id: userId || undefined }) });
      toast.success('Induction recorded');
      reload();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Couldn’t record the induction'));
    } finally { setBusy(null); }
  };

  const H = ({ children }) => (isField ? <h4 style={field.h4}>{children}</h4> : <h4 className="cw-label" style={{ margin: '14px 0 6px' }}>{children}</h4>);
  const Meta = ({ children, style }) => (isField ? <p style={{ ...field.meta, margin: 0, ...style }}>{children}</p> : <p className="cw-meta" style={{ margin: 0, ...style }}>{children}</p>);

  const wrapProps = isField
    ? { style: field.card }
    : { className: 'cw-card cw-card-pad' };

  const title = (
    isField
      ? <h3 style={field.h}>Before you go to site</h3>
      : <h3 className="cw-h3"><i className="fa-solid fa-helmet-safety" aria-hidden="true" style={{ color: 'var(--cw-blue)', marginRight: 6 }} />Site induction and what to bring</h3>
  );

  if (error) {
    return (
      <section {...wrapProps} aria-label="Site induction">
        {title}
        <Meta style={{ marginTop: 6 }}>Couldn’t load the induction details. {apiErrorMessage(error)}</Meta>
        <button type="button" className={isField ? undefined : 'cw-btn cw-btn-secondary'} style={isField ? { ...field.btn, background: '#fff', color: '#1A80E5', marginTop: 8 } : { marginTop: 8 }} onClick={reload}>Try again</button>
      </section>
    );
  }
  if (!hasLoaded) return null;

  const meRow = data.team.find((t) => Number(t.user_id) === Number(data.me));
  const nothing = !data.packs.length && !data.forms.length && !(data.job && data.job.description);

  return (
    <section {...wrapProps} aria-label="Site induction">
      {title}

      {nothing && (
        <Meta style={{ marginTop: 6 }}>
          No induction details for this site yet. {isField ? 'Ask the office before you go.' : 'Link the building to a client and fill in the client’s access and documents to show them here.'}
        </Meta>
      )}

      {data.packs.map((p) => {
        const ind = p.induction;
        const mine = meRow ? meRow.inductions.find((x) => x.client_id === p.client.id) : null;
        return (
          <div key={p.client.id} style={{ marginTop: 10 }}>
            {data.packs.length > 1 && <Meta style={{ fontWeight: 600 }}>{p.client.name}</Meta>}
            {ind && (ind.required || ind.url) ? (
              <div style={isField ? {} : { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={isField ? { display: 'block', fontSize: '0.9rem' } : { fontSize: 'var(--cs-text-base)' }}>
                  <strong>{p.client.name}</strong> {ind.required ? 'requires a site induction' : 'has a site induction'}
                  {ind.renewal_months ? `, renewed every ${plural(ind.renewal_months, 'month')}` : ''}.
                </span>
                {ind.url && <a href={ind.url} target="_blank" rel="noopener noreferrer" className={isField ? undefined : 'cw-btn cw-btn-secondary'} style={isField ? field.link : undefined}>
                  Open the induction{!isField && <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true" style={{ marginLeft: 6 }} />}
                </a>}
              </div>
            ) : (
              <Meta>{p.client.name} hasn’t set a site induction.</Meta>
            )}
            {ind && (ind.required || ind.url) && meRow && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
                <Meta>Your induction: {stateText(mine)}</Meta>
                {!readOnly && data.can_record && mine?.state !== 'current' && (
                  <button type="button" className={isField ? undefined : 'cw-btn cw-btn-primary'} style={isField ? field.btn : undefined}
                    disabled={!!busy} onClick={() => record(p.client.id)}>
                    {busy === `${p.client.id}:me` ? 'Saving…' : 'I’ve done this induction'}
                  </button>
                )}
              </div>
            )}

            {(ind?.site_hours || ind?.parking_notes || ind?.permit_system || ind?.key_holder) && (<>
              <H>Site access</H>
              <List isField={isField} icon="fa-solid fa-circle-info" items={[
                ind.site_hours && `Site hours: ${ind.site_hours}`,
                ind.parking_notes && `Parking: ${ind.parking_notes}`,
                ind.key_holder && `Key holder: ${ind.key_holder}`,
                ind.permit_system && `Permits: ${ind.permit_system}${ind.permit_lead_days ? ` (${plural(ind.permit_lead_days, 'day')} notice)` : ''}`,
              ].filter(Boolean)} />
            </>)}
            {p.ppe.length > 0 && (<><H>PPE to bring</H><List isField={isField} icon="fa-solid fa-helmet-safety" items={p.ppe} /></>)}
            {p.pre_site_checks.length > 0 && (<><H>Pre-site checks</H><List isField={isField} icon="fa-regular fa-square-check" items={p.pre_site_checks} /></>)}
            {p.documents.length > 0 && (<>
              <H>Client documents</H>
              <ul style={{ margin: 0, paddingLeft: isField ? 18 : 0, listStyle: isField ? 'disc' : 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {p.documents.map((d) => (
                  <li key={d.id} style={{ fontSize: isField ? '0.9rem' : 'var(--cs-text-sm)' }}>
                    {!isField && <i className="fa-regular fa-file-lines" aria-hidden="true" style={{ color: 'var(--cw-blue)', width: 18 }} />}
                    {d.url ? <a href={d.url} target="_blank" rel="noopener noreferrer" style={isField ? field.link : undefined}>{d.title}</a> : d.title}
                    {d.doc_type ? <span style={isField ? field.meta : undefined} className={isField ? undefined : 'cw-meta'}> · {d.doc_type}</span> : null}
                    {d.expired && <strong style={{ color: isField ? '#b91c1c' : 'var(--cw-danger-fg)' }}> · out of date</strong>}
                  </li>
                ))}
              </ul>
            </>)}
          </div>
        );
      })}

      {data.forms.length > 0 && (<>
        <H>Forms to fill in on this job</H>
        <List isField={isField} icon="fa-solid fa-clipboard-list"
          items={data.forms.map((f) => `${f.name}${f.submitted ? ' — done' : ''}`)} />
      </>)}

      {data.job?.description && (<>
        <H>Job description</H>
        <Meta style={{ whiteSpace: 'pre-wrap', color: isField ? '#111827' : 'var(--cw-text-2)' }}>{data.job.description}</Meta>
      </>)}

      {!isField && data.team.length > 0 && data.packs.some((p) => p.induction?.required || p.induction?.url) && (<>
        <H>Team inductions</H>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {data.team.map((t) => t.inductions.map((st) => {
            const client = data.packs.find((p) => p.client.id === st.client_id)?.client;
            const key = `${st.client_id}:${t.user_id}`;
            return (
              <li key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ flex: '1 1 160px', fontWeight: 600, fontSize: 'var(--cs-text-sm)' }}>{t.name}{data.packs.length > 1 && client ? <span className="cw-meta" style={{ fontWeight: 400 }}> · {client.name}</span> : null}</span>
                <span className="cw-meta" style={{ color: st.state === 'current' ? 'var(--cw-success-fg)' : st.state === 'expired' ? 'var(--cw-danger-fg)' : 'var(--cw-warning-fg)' }}>{stateText(st)}</span>
                {!readOnly && data.can_record_others && st.state !== 'current' && (
                  <button type="button" className="cw-btn-link" disabled={!!busy} onClick={() => record(st.client_id, t.user_id)}>
                    {busy === key ? 'Saving…' : 'Mark as done today'}
                  </button>
                )}
              </li>
            );
          }))}
        </ul>
      </>)}
    </section>
  );
};

export default JobInductionPack;
